/**
 * Заказы поставщикам (PO) — Phase 2.
 *
 * В оригинальной экселе это «Отложено_приход». Жизненный цикл:
 *   draft (создан) → sent (отправлен поставщику) → partial / received
 *   (полностью/частично принят) → (опц.) cancelled.
 *
 * При приёмке (полной или частичной) — автоматически создаётся
 * приходная накладная (warehouse_inflow_docs).
 *
 * UI: реестр PO с правой панелью детали выбранного заказа. Кнопка
 * «Создать» открывает модалку. Из открытых заказов on_order попадает
 * в страницу Планирования как «уже едет».
 */
import { useMemo, useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Icon } from "@/components/prototype/Icon";
import { Spinner } from "@/components/Spinner";
import { ItemAutocomplete } from "@/components/warehouse/ItemAutocomplete";
import { usePermissions } from "@/lib/usePermissions";
import {
  fetchPurchaseOrders, fetchPurchaseOrder, createPurchaseOrder,
  updatePOStatus, cancelPurchaseOrder, receivePurchaseOrder,
  fetchContractors,
  formatRub, PO_STATUS_LABEL,
  type POItemRow, type POStatus, type ItemRow, type ContractorRow,
} from "@/api/warehouse";
import { ApiError } from "@/api/client";


const STATUS_FILTERS: { value: ""; label: string }[]
  | { value: POStatus | ""; label: string }[] = [
  { value: "" as const,        label: "Все" },
  { value: "draft",      label: PO_STATUS_LABEL.draft },
  { value: "sent",       label: PO_STATUS_LABEL.sent },
  { value: "partial",    label: PO_STATUS_LABEL.partial },
  { value: "received",   label: PO_STATUS_LABEL.received },
  { value: "cancelled",  label: PO_STATUS_LABEL.cancelled },
];

const STATUS_COLOR: Record<POStatus, string> = {
  draft:     "var(--mute)",
  sent:      "#3390ec",
  partial:   "#e99a12",
  received:  "#3D7A3D",
  cancelled: "var(--brick)",
};


export function WarehousePurchaseOrdersPage() {
  const can = usePermissions();
  const [statusFilter, setStatusFilter] = useState<POStatus | "">("");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [creatingOpen, setCreatingOpen] = useState(false);

  const listQ = useQuery({
    queryKey: ["warehouse-po-list", statusFilter],
    queryFn: () => fetchPurchaseOrders({ status: statusFilter || undefined, limit: 200 }),
  });

  if (listQ.isLoading) return <Spinner size="lg" label="Загрузка заказов поставщикам…" />;
  if (listQ.error) {
    const err = listQ.error;
    if (err instanceof ApiError && err.status === 403) {
      return <div className="alert alert-amber"><div className="alert-body">Доступ ограничен</div></div>;
    }
    return <div className="alert alert-red"><div className="alert-body">{(err as Error).message}</div></div>;
  }
  const list = listQ.data?.items || [];

  return (
    <div className="page">

      <div className="filter-bar" style={{ justifyContent: "space-between", marginBottom: "var(--gap)", flexWrap: "wrap", gap: 8 }}>
        <div className="chip-row">
          {STATUS_FILTERS.map(f => (
            <button key={f.value || "all"} type="button"
              className={`chip ${statusFilter === f.value ? "active" : ""}`}
              onClick={() => setStatusFilter(f.value as POStatus | "")}>
              {f.label}
            </button>
          ))}
        </div>
        {can("po.create") && (
          <button type="button" className="btn btn-primary"
            onClick={() => setCreatingOpen(true)}
            style={{ background: "var(--brick)", color: "#fff", border: "none", padding: "6px 14px", fontWeight: 600, fontSize: 13, cursor: "pointer" }}>
            + Новый заказ
          </button>
        )}
      </div>

      {list.length === 0 ? (
        <div className="card" style={{ textAlign: "center", padding: "40px 20px" }}>
          <div className="muted">Заказов поставщикам пока нет</div>
          <div style={{ fontSize: 12, color: "var(--mute)", marginTop: 8 }}>
            Создайте первый заказ — его открытые позиции появятся в «Планирование» как «уже заказано у поставщика».
          </div>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: selectedId != null ? "1fr 1fr" : "1fr", gap: "var(--gap)" }}>
          {/* Реестр */}
          <div className="card" style={{ padding: 0 }}>
            <div className="tbl-scroll">
              <table className="tbl">
                <thead>
                  <tr>
                    <th style={{ width: 60 }}>№</th>
                    <th>Поставщик</th>
                    <th style={{ width: 120 }}>Статус</th>
                    <th style={{ width: 110 }}>Заказан</th>
                    <th style={{ width: 110 }}>Ожидается</th>
                    <th className="num" style={{ width: 110 }}>Сумма</th>
                    <th className="num" style={{ width: 110 }}>Поз. / в пути</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map(po => (
                    <tr key={po.id}
                        onClick={() => setSelectedId(po.id)}
                        style={{ cursor: "pointer", background: po.id === selectedId ? "var(--paper)" : undefined }}>
                      <td className="tabular">{po.doc_number || po.id}</td>
                      <td>{po.supplier_name || <span className="muted">—</span>}</td>
                      <td>
                        <span style={{ display: "inline-block", padding: "1px 8px", fontSize: 11,
                                        borderRadius: 10, background: "var(--paper)",
                                        color: STATUS_COLOR[po.status], fontWeight: 600 }}>
                          {PO_STATUS_LABEL[po.status]}
                        </span>
                      </td>
                      <td className="tabular" style={{ fontSize: 12 }}>{po.order_date}</td>
                      <td className="tabular" style={{ fontSize: 12 }}>{po.expected_date || <span className="muted">—</span>}</td>
                      <td className="num tabular">{formatRub(po.total_amount)}</td>
                      <td className="num tabular" style={{ fontSize: 12 }}>
                        {po.items_count}{po.pending_qty > 0 && <span style={{ color: "var(--brick)" }}> · ↻{po.pending_qty}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {selectedId != null && (
            <PODetails id={selectedId} onClose={() => setSelectedId(null)} />
          )}
        </div>
      )}

      {creatingOpen && (
        <POCreateModal onClose={() => setCreatingOpen(false)}
                       onCreated={(id) => { setSelectedId(id); setCreatingOpen(false); }} />
      )}
    </div>
  );
}


function PODetails({ id, onClose }: { id: number; onClose: () => void }) {
  const can = usePermissions();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["warehouse-po", id],
    queryFn: () => fetchPurchaseOrder(id),
  });

  const [receiveOpen, setReceiveOpen] = useState(false);

  const setStatusMut = useMutation({
    mutationFn: (status: POStatus) => updatePOStatus(id, status),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["warehouse-po", id] });
      void qc.invalidateQueries({ queryKey: ["warehouse-po-list"] });
      void qc.invalidateQueries({ queryKey: ["warehouse-planning"] });
    },
  });
  const cancelMut = useMutation({
    mutationFn: () => cancelPurchaseOrder(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["warehouse-po-list"] });
      void qc.invalidateQueries({ queryKey: ["warehouse-planning"] });
      onClose();
    },
  });

  if (q.isLoading) return <div className="card" style={{ padding: 16 }}><Spinner /></div>;
  if (!q.data) return null;
  const { doc, items } = q.data;
  const canEditStatus = !["received", "cancelled"].includes(doc.status);
  const canReceive = ["sent", "partial"].includes(doc.status);

  return (
    <div className="card" style={{ padding: 16, position: "relative" }}>
      <button type="button" onClick={onClose}
        style={{ position: "absolute", top: 8, right: 8, background: "transparent", border: "none", fontSize: 20, color: "var(--mute)", cursor: "pointer" }}>×</button>
      <div style={{ fontFamily: "var(--serif)", fontSize: 20, fontWeight: 700 }}>
        Заказ № {doc.doc_number || `#${doc.id}`}
      </div>
      <div style={{ fontSize: 12, color: "var(--mute)", marginTop: 4, marginBottom: 12 }}>
        {doc.supplier_name && <>Поставщик: <strong>{doc.supplier_name}</strong> · </>}
        Заказан: {doc.order_date}{doc.expected_date && <> · Ожидается: {doc.expected_date}</>}
        {doc.warehouse && <> · Склад: {doc.warehouse}</>}
        {doc.note && <div style={{ marginTop: 4 }}>{doc.note}</div>}
      </div>

      {/* Статус + действия */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
        <span style={{ fontSize: 12, color: "var(--mute)" }}>Статус:</span>
        {canEditStatus ? (
          <select value={doc.status} onChange={(e) => setStatusMut.mutate(e.target.value as POStatus)} className="input-sm">
            <option value="draft">{PO_STATUS_LABEL.draft}</option>
            <option value="sent">{PO_STATUS_LABEL.sent}</option>
            <option value="partial">{PO_STATUS_LABEL.partial}</option>
            <option value="received">{PO_STATUS_LABEL.received}</option>
          </select>
        ) : (
          <span style={{ fontWeight: 600, color: STATUS_COLOR[doc.status] }}>{PO_STATUS_LABEL[doc.status]}</span>
        )}
        <div style={{ flex: 1 }}/>
        {canReceive && can("po.receive") && (
          <button type="button" className="btn btn-sm btn-primary"
                  onClick={() => setReceiveOpen(true)}>
            <Icon name="arrow_down" size={12}/> Принять
          </button>
        )}
        {!["cancelled", "received"].includes(doc.status) && can("po.create") && (
          <button type="button" className="btn btn-sm"
                  onClick={() => { if (confirm(`Отменить заказ № ${doc.doc_number || doc.id}?`)) cancelMut.mutate(); }}
                  style={{ color: "var(--brick)" }}>
            Отменить
          </button>
        )}
      </div>

      <div className="tbl-scroll">
        <table className="tbl">
          <thead>
            <tr>
              <th style={{ width: 110 }}>Артикул</th>
              <th>Наименование</th>
              <th style={{ width: 70 }}>Ед.</th>
              <th className="num" style={{ width: 90 }}>Заказано</th>
              <th className="num" style={{ width: 90 }}>Получено</th>
              <th className="num" style={{ width: 90 }}>Осталось</th>
              <th className="num" style={{ width: 100 }}>Цена</th>
              <th className="num" style={{ width: 110 }}>Сумма</th>
            </tr>
          </thead>
          <tbody>
            {items.map(it => {
              const left = it.quantity_ordered - it.quantity_received;
              return (
                <tr key={it.id}>
                  <td className="tabular">{it.sku || "—"}</td>
                  <td>{it.name}</td>
                  <td>{it.unit || "—"}</td>
                  <td className="num tabular">{it.quantity_ordered}</td>
                  <td className="num tabular">{it.quantity_received}</td>
                  <td className="num tabular strong" style={{ color: left > 0 ? "var(--brick)" : "var(--mute)" }}>
                    {left > 0 ? left : "—"}
                  </td>
                  <td className="num tabular">{it.price != null ? formatRub(it.price) : "—"}</td>
                  <td className="num tabular">{it.amount != null ? formatRub(it.amount) : "—"}</td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={7} style={{ textAlign: "right", padding: "8px 12px", fontWeight: 700 }}>Итого:</td>
              <td className="num tabular strong">{formatRub(doc.total_amount)}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      {receiveOpen && (
        <POReceiveModal
          poId={doc.id}
          items={items}
          onClose={() => setReceiveOpen(false)}
          onReceived={() => {
            void qc.invalidateQueries({ queryKey: ["warehouse-po", id] });
            void qc.invalidateQueries({ queryKey: ["warehouse-po-list"] });
            void qc.invalidateQueries({ queryKey: ["warehouse-planning"] });
            void qc.invalidateQueries({ queryKey: ["warehouse-stocks"] });
            void qc.invalidateQueries({ queryKey: ["warehouse-overview"] });
            void qc.invalidateQueries({ queryKey: ["warehouse-inflow-list"] });
            setReceiveOpen(false);
          }}
        />
      )}
    </div>
  );
}


function POReceiveModal({ poId, items, onClose, onReceived }: {
  poId: number;
  items: POItemRow[];
  onClose: () => void;
  onReceived: () => void;
}) {
  const [qty, setQty] = useState<Record<number, string>>(
    Object.fromEntries(items.map(it => [it.item_id, String(it.quantity_ordered - it.quantity_received)]))
  );
  const [createInflow, setCreateInflow] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: () => {
      const lines = items
        .map(it => {
          const v = parseFloat((qty[it.item_id] || "").replace(",", "."));
          if (isNaN(v) || v <= 0) return null;
          return { item_id: it.item_id, quantity_received: v, price: it.price };
        })
        .filter((x): x is { item_id: number; quantity_received: number; price: number | null } => x !== null);
      if (lines.length === 0) throw new Error("Введите хотя бы одно положительное количество");
      return receivePurchaseOrder(poId, lines, createInflow);
    },
    onSuccess: onReceived,
    onError: (e) => setError(e instanceof Error ? e.message : String(e)),
  });

  return (
    <div role="dialog" onClick={onClose}
         style={{ position: "fixed", inset: 0, zIndex: 100, background: "rgba(0,0,0,0.4)", display: "grid", placeItems: "center", padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()} className="card"
           style={{ width: "100%", maxWidth: 700, padding: 20, maxHeight: "85vh", overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
          <div style={{ fontFamily: "var(--serif)", fontSize: 20, fontWeight: 700 }}>
            Приём заказа № {poId}
          </div>
          <button type="button" onClick={onClose} style={{ background: "transparent", border: "none", fontSize: 20, cursor: "pointer", color: "var(--mute)" }}>×</button>
        </div>
        <div style={{ fontSize: 12, color: "var(--mute)", marginBottom: 12 }}>
          Введите фактическое полученное количество. По умолчанию — «осталось».
          Можно принять частично — статус автоматически станет «Частично», остаток продолжит висеть в открытых.
        </div>
        <div className="tbl-scroll" style={{ marginBottom: 12 }}>
          <table className="tbl">
            <thead>
              <tr>
                <th>Позиция</th>
                <th className="num">Заказано</th>
                <th className="num">Уже принято</th>
                <th className="num" style={{ width: 130 }}>Принять сейчас</th>
              </tr>
            </thead>
            <tbody>
              {items.map(it => {
                const left = it.quantity_ordered - it.quantity_received;
                return (
                  <tr key={it.id} style={left <= 0 ? { opacity: 0.5 } : undefined}>
                    <td>{it.sku && <span className="tabular" style={{ color: "var(--mute)", marginRight: 6 }}>{it.sku}</span>}{it.name}</td>
                    <td className="num tabular">{it.quantity_ordered} {it.unit || ""}</td>
                    <td className="num tabular">{it.quantity_received}</td>
                    <td className="num">
                      <input type="text" inputMode="decimal" className="input-sm"
                             value={qty[it.item_id] ?? ""}
                             onChange={e => setQty(d => ({ ...d, [it.item_id]: e.target.value }))}
                             disabled={left <= 0}
                             style={{ width: "100%", textAlign: "right" }} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, marginBottom: 12 }}>
          <input type="checkbox" checked={createInflow} onChange={e => setCreateInflow(e.target.checked)} />
          Создать приходную накладную (рекомендуется)
        </label>
        {error && <div className="alert alert-red" style={{ marginBottom: 8 }}><div className="alert-body">{error}</div></div>}
        <div style={{ textAlign: "right" }}>
          <button type="button" className="btn" onClick={onClose} style={{ marginRight: 8 }}>Отмена</button>
          <button type="button" className="btn btn-primary" onClick={() => mut.mutate()} disabled={mut.isPending}
                  style={{ background: "#3D7A3D", color: "#fff", border: "none", padding: "6px 18px", fontWeight: 600, cursor: "pointer" }}>
            {mut.isPending ? "Приём…" : "Принять"}
          </button>
        </div>
      </div>
    </div>
  );
}


function POCreateModal({ onClose, onCreated }: { onClose: () => void; onCreated: (id: number) => void }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    supplier_id: null as number | null,
    supplier_name: "",
    order_date: today,
    expected_date: "",
    warehouse: "СКЛАД 2_4",
    note: "",
  });
  const [lines, setLines] = useState<{ item: ItemRow; quantity: number; price: number }[]>([]);
  const [error, setError] = useState<string | null>(null);

  const suppliersQ = useQuery({
    queryKey: ["warehouse-contractors", "supplier"],
    queryFn: () => fetchContractors({ kind: "supplier" }),
  });
  const suppliers: ContractorRow[] = suppliersQ.data || [];

  const total = useMemo(
    () => lines.reduce((s, l) => s + l.quantity * (l.price || 0), 0),
    [lines]
  );

  const mut = useMutation({
    mutationFn: () => createPurchaseOrder({
      supplier_id: form.supplier_id,
      supplier_name: form.supplier_name.trim() || null,
      order_date: form.order_date,
      expected_date: form.expected_date || null,
      warehouse: form.warehouse,
      note: form.note.trim() || null,
      items: lines.map(l => ({ item_id: l.item.id, quantity: l.quantity, price: l.price || null })),
    }),
    onSuccess: (data) => onCreated(data.id),
    onError: (e) => setError(e instanceof Error ? e.message : (e as ApiError).body ? JSON.stringify((e as ApiError).body) : String(e)),
  });

  function addItem(it: ItemRow) {
    setLines(ls => {
      const i = ls.findIndex(l => l.item.id === it.id);
      if (i !== -1) {
        const updated = [...ls];
        updated[i] = { ...updated[i], quantity: updated[i].quantity + 1 };
        return updated;
      }
      return [...ls, { item: it, quantity: 1, price: it.price_buy ?? 0 }];
    });
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!lines.length) { setError("Добавьте хотя бы одну позицию"); return; }
    mut.mutate();
  }

  return (
    <div role="dialog" onClick={onClose}
         style={{ position: "fixed", inset: 0, zIndex: 100, background: "rgba(0,0,0,0.4)", display: "grid", placeItems: "center", padding: 16 }}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={onSubmit} className="card"
            style={{ width: "100%", maxWidth: 800, padding: 20, maxHeight: "85vh", overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
          <div style={{ fontFamily: "var(--serif)", fontSize: 22, fontWeight: 700 }}>Новый заказ поставщику</div>
          <button type="button" onClick={onClose} style={{ background: "transparent", border: "none", fontSize: 20, cursor: "pointer", color: "var(--mute)" }}>×</button>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
          <div>
            <Label>Поставщик</Label>
            <select className="input-sm" style={{ width: "100%" }}
              value={form.supplier_id ?? ""}
              onChange={(e) => {
                const v = e.target.value;
                if (v === "") { setForm({ ...form, supplier_id: null, supplier_name: "" }); return; }
                const id = parseInt(v, 10);
                const s = suppliers.find(x => x.id === id);
                // Если у поставщика задан default_lead_days — авто-подставляем
                // expected_date = order_date + lead_days (если ещё не задан).
                const next = { ...form, supplier_id: id, supplier_name: s?.name || "" };
                if (s?.default_lead_days && !form.expected_date) {
                  const base = new Date(form.order_date + "T00:00:00");
                  base.setDate(base.getDate() + s.default_lead_days);
                  next.expected_date = base.toISOString().slice(0, 10);
                }
                setForm(next);
              }}>
              <option value="">— Без привязки —</option>
              {suppliers.map(s => (
                <option key={s.id} value={s.id}>
                  {s.name}{s.default_lead_days ? ` (≈${s.default_lead_days} дн)` : ""}
                </option>
              ))}
            </select>
            <input type="text" className="input-sm" style={{ width: "100%", marginTop: 4 }}
              value={form.supplier_name}
              onChange={(e) => setForm({ ...form, supplier_name: e.target.value })}
              placeholder='или ввести вручную, например «ООО "МетСнаб"»' />
          </div>
          <div>
            <Label>Склад</Label>
            <select className="input-sm" style={{ width: "100%" }} value={form.warehouse}
                    onChange={e => setForm({ ...form, warehouse: e.target.value })}>
              <option value="СКЛАД 2_4">СКЛАД 2_4</option>
              <option value="ИНСТРУМЕНТ 1.0">ИНСТРУМЕНТ 1.0</option>
              <option value="ОФИС">ОФИС</option>
            </select>
          </div>
          <div>
            <Label>Дата заказа</Label>
            <input type="date" className="input-sm" style={{ width: "100%" }} value={form.order_date}
                   onChange={e => setForm({ ...form, order_date: e.target.value })} />
          </div>
          <div>
            <Label>Ожидаемая дата прихода</Label>
            <input type="date" className="input-sm" style={{ width: "100%" }} value={form.expected_date}
                   onChange={e => setForm({ ...form, expected_date: e.target.value })} />
          </div>
        </div>

        <Label>Примечание</Label>
        <textarea className="input-sm" style={{ width: "100%", minHeight: 50, marginBottom: 12 }}
                  value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} />

        <div style={{ marginBottom: 8, padding: 8, background: "var(--paper)", borderRadius: 4, display: "flex", alignItems: "center", gap: 8 }}>
          <Icon name="search" size={14} />
          <span style={{ fontSize: 12, color: "var(--mute)" }}>Добавить позицию:</span>
          <div style={{ flex: 1, maxWidth: 500 }}>
            <ItemAutocomplete onSelect={addItem} placeholder="Поиск по артикулу или наименованию" />
          </div>
        </div>

        <div className="tbl-scroll" style={{ marginBottom: 12 }}>
          <table className="tbl">
            <thead>
              <tr>
                <th>Позиция</th>
                <th className="num" style={{ width: 100 }}>Кол-во</th>
                <th className="num" style={{ width: 100 }}>Цена</th>
                <th className="num" style={{ width: 110 }}>Сумма</th>
                <th style={{ width: 36 }}/>
              </tr>
            </thead>
            <tbody>
              {lines.length === 0 ? (
                <tr><td colSpan={5} style={{ textAlign: "center", padding: 30, color: "var(--mute)" }}>
                  Добавьте первую позицию через поиск выше
                </td></tr>
              ) : lines.map((l, idx) => (
                <tr key={l.item.id}>
                  <td>
                    {l.item.sku && <span className="tabular" style={{ color: "var(--mute)", marginRight: 6 }}>{l.item.sku}</span>}
                    {l.item.name}
                  </td>
                  <td className="num">
                    <input type="text" inputMode="decimal" className="input-sm" style={{ width: "100%", textAlign: "right" }}
                           value={l.quantity}
                           onChange={e => {
                             const v = parseFloat(e.target.value.replace(",", ".")) || 0;
                             setLines(ls => ls.map((x, i) => i === idx ? { ...x, quantity: v } : x));
                           }} />
                  </td>
                  <td className="num">
                    <input type="text" inputMode="decimal" className="input-sm" style={{ width: "100%", textAlign: "right" }}
                           value={l.price}
                           onChange={e => {
                             const v = parseFloat(e.target.value.replace(",", ".")) || 0;
                             setLines(ls => ls.map((x, i) => i === idx ? { ...x, price: v } : x));
                           }} />
                  </td>
                  <td className="num tabular strong">{formatRub(l.quantity * l.price)}</td>
                  <td><button type="button" className="btn btn-sm" onClick={() => setLines(ls => ls.filter((_, i) => i !== idx))} style={{ color: "var(--brick)" }}>×</button></td>
                </tr>
              ))}
            </tbody>
            {lines.length > 0 && (
              <tfoot>
                <tr>
                  <td colSpan={3} style={{ textAlign: "right", padding: "8px 12px", fontWeight: 700 }}>Итого:</td>
                  <td className="num tabular strong">{formatRub(total)}</td>
                  <td/>
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        {error && <div className="alert alert-red" style={{ marginBottom: 8 }}><div className="alert-body">{error}</div></div>}

        <div style={{ textAlign: "right" }}>
          <button type="button" className="btn" onClick={onClose} style={{ marginRight: 8 }}>Отмена</button>
          <button type="submit" className="btn btn-primary" disabled={mut.isPending || !lines.length}
                  style={{ background: "var(--brick)", color: "#fff", border: "none", padding: "6px 18px", fontWeight: 600, cursor: "pointer" }}>
            {mut.isPending ? "Создание…" : "Создать (черновик)"}
          </button>
        </div>
      </form>
    </div>
  );
}


function Label({ children }: { children: React.ReactNode }) {
  return <div style={{ fontSize: 12, fontWeight: 600, color: "var(--ink-3)", marginBottom: 4 }}>{children}</div>;
}
